// redux/dashboard/dashboardActions.js

import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import {
  clockInApi,
  clockOutApi,
  fetchActiveTasksApi,
  fetchAttendanceDashboardApi,
  fetchClientStatusApi,
  fetchQueriesCountAndProductivityStarApi,
  fetchQueryTypeApi,
  fetchSummaryApi,
  fetchUserStatusApi,
  getBreakBreakdownApi,
  getPunchingReportApi,
  getStatusClockApi,
  getTeamClockStatusApi,
  resumeSessionApi,
  workingHoursApi,
  fetchPunchSpellsApi,
  fetchSummaryByProjectApi,
} from "./dashboardApi";
import { teamPendingTasksApi } from "./dashboardApi";
import { showApiErrorToast } from "../../utils/toastErrorHandler";

// Helper to handle errors DRY (Don't Repeat Yourself)
const handleError = (err, rejectWithValue) => {
  // Caution: Removing token on ANY error might be aggressive. 
  // Usually you only do this for 401 errors.
  // localStorage.removeItem("token");
  // localStorage.removeItem("user");
  if (err.response?.status === 401) {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
}
  showApiErrorToast(err?.response?.data?.error || err.message);
  return rejectWithValue(err?.response?.data?.message || err.message);
};

export const dashboardClientStatusListsApis = createAsyncThunk(
  "dashboard/fetchClientStatus",
  async (payload, { rejectWithValue }) => {
    try {
      return await fetchClientStatusApi(payload);
    } catch (err) {
      return handleError(err, rejectWithValue);
    }
  }
);

export const dashboardQueryTypeListsApis = createAsyncThunk(
  "dashboard/fetchQueryType",
  async (payload, { rejectWithValue }) => {
    try {
      return await fetchQueryTypeApi(payload);
    } catch (err) {
      return handleError(err, rejectWithValue);
    }
  }
);

export const dashboardSummaryListsApis = createAsyncThunk(
  "dashboard/fetchSummary",
  async (payload, { rejectWithValue }) => {
    try {
      return await fetchSummaryApi(payload);
    } catch (err) {
      return handleError(err, rejectWithValue);
    }
  }
);

export const dashboarUserStatusListsApis = createAsyncThunk(
  "dashboard/fetchUserStatus",
  async (payload, { rejectWithValue }) => {
    try {
      return await fetchUserStatusApi(payload);
    } catch (err) {
      return handleError(err, rejectWithValue);
    }
  }
);

export const dashboardQueriesCountAndProductivityStarApis = createAsyncThunk(
  "dashboard/fetchQueriesCountAndProductivityStar",
  async (payload, { rejectWithValue }) => {
    try {
      return await fetchQueriesCountAndProductivityStarApi(payload);
    } catch (err) {
      return handleError(err, rejectWithValue);
    }
  }
);

export const clockInEmployee = createAsyncThunk(
  "attendance/clockInEmployee",
  async (payload, { rejectWithValue }) => {
    try {
      
      return await clockInApi(payload);
    } catch (error) {
      return handleError(error,rejectWithValue);
    }
  }
);
 
export const clockOutEmployee = createAsyncThunk(
  "attendance/clockOutEmployee",
  async (payload, {rejectWithValue}) => {
    try{
      return await clockOutApi(payload);
    }catch(error){
      return handleError(error,rejectWithValue);
    }
  }
);

export const getWorkingHours = createAsyncThunk(
  "attendance/getWorkingHours",
  async (payload, {rejectWithValue}) => {
    try{
      return await workingHoursApi(payload);
    }catch(error){
      return handleError(error,rejectWithValue);
    }
  }
);

export const getAttendanceDashboard = createAsyncThunk(
  "dashboard/getAttendanceDashboard",
  async (payload, { rejectWithValue }) => {
    try {
      return await fetchAttendanceDashboardApi(payload);
    } catch (error) {
      return handleError(error, rejectWithValue);
    }
  }
);

export const getStatusClock = createAsyncThunk(
  "dashboard/clock_status",

  async (_, {rejectWithValue}) => {
    try{
      const response =  await getStatusClockApi();

      console.log("Clock Status API Response", response.data);

      return response.data;
    } catch(error) {
      console.error("Clock status API error",error);

      return rejectWithValue(error?.response?.data || {
        message: "Unable to get clock status"
      })
    }
  }
)





/* Resume the session on page load.
 *
 * Never toasts. It fires on every load, nobody asked to be told about it, and
 * its normal outcome is "nothing needed doing" — the tab-close punch was
 * removed and the person was never actually off the clock. */
export const resumeSession = createAsyncThunk(
  "dashboard/resume_session",
  async (_, { rejectWithValue }) => {
    try {
      const response = await resumeSessionApi();
      return response.data;
    } catch (error) {
      /* 404 — this API predates /attendance/resume-session.
       *
       * The endpoint has its own fallback for a missing stored procedure, and
       * that was the wrong place for it: a route the server does not have
       * cannot fall back to anything. So the frontend shipped, the API did
       * not, and every page load asked for a route that answered 404. The
       * tab-close beacon kept writing its clock-out — that half is frontend
       * only — and nothing was left to undo it or to clock anybody back in.
       * People sat clocked out for twenty minutes at a time.
       *
       * Falling back to the plain clock-in it replaced makes the deploy order
       * stop mattering. It spends a punch per refresh, which is the very
       * thing resume-session exists to avoid, but a wasted punch is nothing
       * beside an account wrongly clocked out.
       *
       * Only 404. Any other failure is a real one and is reported as such. */
      if (error?.response?.status === 404) {
        try {
          const user = JSON.parse(localStorage.getItem("user") || "{}");
          if (user?.userId) {
            const res = await clockInApi(user.userId);
            return res.data;
          }
        } catch {
          /* The fallback failing changes nothing the caller can act on:
             attendance is a side effect of using the app, and it must not
             stop somebody working. */
        }
      }

      return rejectWithValue(
        error?.response?.data || { message: "Resume failed" },
      );
    }
  },
);

/* Who is clocked in, out, or not in yet — everybody, for one day.
   Takes { onDate } or nothing, in which case the procedure reads today. */
export const getTeamClockStatus = createAsyncThunk(
  "dashboard/team_clock_status",
  async (params, { rejectWithValue }) => {
    try {
      const response = await getTeamClockStatusApi(params);
      return response.data;
    } catch (error) {
      showApiErrorToast(error?.response?.data?.error || error.message);
      return rejectWithValue(
        error?.response?.data || { message: "Unable to get team clock status" },
      );
    }
  },
);

/* Everything running right now. Polled by the dashboard panel, so it stays
   deliberately thin — no date range, no paging, one small recordset. */
export const dashboardActiveTasksApis = createAsyncThunk(
  "dashboard/activeTasks",
  async (_, { rejectWithValue }) => {
    try {
      return await fetchActiveTasksApi();
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || err.message);
    }
  },
);


/* How the day's away-from-desk time splits by reason. */
export const getBreakBreakdown = createAsyncThunk(
  "dashboard/break_breakdown",
  async (params, { rejectWithValue }) => {
    try {
      const response = await getBreakBreakdownApi(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data || { message: "Unable to load break breakdown" },
      );
    }
  },
);


/* Attendance rows for the Punching Report. */
export const getPunchingReport = createAsyncThunk(
  "dashboard/punching_report",
  async (params, { rejectWithValue }) => {
    try {
      const response = await getPunchingReportApi(params);
      return response.data;
    } catch (error) {
      showApiErrorToast(error?.response?.data?.error || error.message);
      return rejectWithValue(
        error?.response?.data || { message: "Unable to load the punching report" },
      );
    }
  },
);

/* Fetched on demand, and NOT parked in the slice.
 *
 * The list belongs to one open dialog: it is thrown away when that closes, and
 * keeping it in redux would leave the previous team's rows on screen for a
 * frame the next time one is opened — which is exactly the sort of stale-data
 * flash that makes a count and its list look like they disagree. */
export const teamPendingTasksApis = createAsyncThunk(
  "dashboard/teamPendingTasks",
  async (params, { rejectWithValue }) => {
    try {
      return await teamPendingTasksApi(params);
    } catch (err) {
      const message =
        err?.response?.data?.message || err?.message || "Could not load the tasks";
      showApiErrorToast(message);
      return rejectWithValue(message);
    }
  },
);

export const punchSpellsApis = createAsyncThunk(
  "dashboard/punchSpells",
  async (payload, { rejectWithValue }) => {
    try {
      return await fetchPunchSpellsApi(payload);
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  },
);

export const summaryByProjectApis = createAsyncThunk(
  "dashboard/summaryByProject",
  async (payload, { rejectWithValue }) => {
    try {
      return await fetchSummaryByProjectApi(payload);
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  },
);
